/**
 * Customer display name helpers
 * Handles proper formatting of customer names based on customer type
 */

export interface CustomerDisplayData {
  customer_type?: 'residential' | 'commercial' | string;
  name?: string;
  last_name?: string;
  business_name?: string | null;
  representative_first_name?: string | null;
  representative_last_name?: string | null;
}

/**
 * Get the display name for a customer based on their type
 * 
 * For commercial customers with a business_name: "ABC Corporation (John Smith)"
 * For commercial customers without a business_name: Falls back to representative name or name/last_name
 * For residential customers: "John Smith" (name + last_name)
 */
export function getCustomerDisplayName(customer: CustomerDisplayData | null | undefined): string {
  if (!customer) return 'No customer';
  
  const isCommercial = customer.customer_type === 'commercial';
  
  if (isCommercial) {
    // For commercial customers, prefer business_name
    if (customer.business_name) {
      // Build representative name from available fields
      const repFirstName = customer.representative_first_name || customer.name || '';
      const repLastName = customer.representative_last_name || customer.last_name || '';
      const repName = `${repFirstName} ${repLastName}`.trim();
      
      if (repName) {
        return `${customer.business_name} (${repName})`;
      }
      return customer.business_name;
    }
    // Fallback: if no business_name, use representative name or regular name
    const repFirstName = customer.representative_first_name || customer.name || '';
    const repLastName = customer.representative_last_name || customer.last_name || '';
    const fallbackName = `${repFirstName} ${repLastName}`.trim();
    return fallbackName || 'Unnamed Customer';
  }
  
  // Residential customers: use name + last_name
  return `${customer.name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed Customer';
}

/**
 * Get a short display name for a customer (without representative in parentheses)
 * For commercial: just business_name or fallback
 * For residential: name + last_name
 */
export function getCustomerShortName(customer: CustomerDisplayData | null | undefined): string {
  if (!customer) return 'No customer';
  
  const isCommercial = customer.customer_type === 'commercial';
  
  if (isCommercial && customer.business_name) {
    return customer.business_name;
  }
  
  // Fallback for commercial without business_name, or residential
  return `${customer.name || ''} ${customer.last_name || ''}`.trim() || 'Unnamed Customer';
}
